import type { Ssm5Session } from '@packages/domain/ssm5';

import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ssm5TextStyles } from '../ssm5.styles';
import { Ssm5ConnectionBadge } from './ssm5-connection-badge';

/** Props for {@link Ssm5ConnectedPanel}. */
export type Ssm5ConnectedPanelProps = {
    /** Whether the session is still open; a lost one keeps its ID readable but is no longer current. */
    isConnected: boolean;
    /** The session that was opened. */
    session: Ssm5Session;
};

const useStyles = createUseStyles({
    strip: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
    },
});

/**
 * The session identity strip of the page, connected or lost. The session body shows it over the reading
 * loader, next to the Status and DTCs panel.
 *
 * @param props - {@link Ssm5ConnectedPanelProps}
 * @returns the connected panel
 */
export function Ssm5ConnectedPanel({ isConnected, session }: Ssm5ConnectedPanelProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <div className={classes.strip}>
            <Typography color="textDarkSecondary" style={ssm5TextStyles.body} variant="default">
                {t('ssm5:connect.idLabel')}
            </Typography>
            <Typography style={ssm5TextStyles.bodySemibold} variant="default">
                {session.cloudConnectorId}
            </Typography>
            <Ssm5ConnectionBadge isConnected={isConnected} />
        </div>
    );
}
